import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import Select from "react-select";
import { DatePicker } from "antd";
import CommonTagsInput from "../../../core/common/Taginput";
import JoditEditor from "jodit-react";
import { useParams } from "react-router";

const EditProject = () => {

    const [tags, setTags] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const editor = useRef(null);
    const [content, setContent] = useState("");
    const [callApiAgain, setCallApiAgain] = useState(1);
    const { projectId } = useParams<{ projectId: string }>();
    const [data, setData] = useState({
        project_id: "",
        project_name: "",
        owner_name: "",
        priority: "",
        cost_center: "",
        department: "",
        attachment_file: "",
        status: ""
    });

    const {
        data: department,
        loading: departmentLoading,
        error: departmentError,
    } = useApi("/api/manager/view-department", {
        method: "GET",
        autoCall: true,
    });

    const departmentData = department?.data;

    const filterData = departmentData?.filter((item: any, index: any) => {
        return item.status === "1";
    });

    const DepartmentSelectData =
        filterData?.map((item: any) => ({
            value: item.ID,
            label: `${item.department}`,
        })) || [];

    const {
        data: costcenter,
        loading: costcenterLoading,
        error: costcenterError,
    } = useApi("/api/master/get-costcenter", {
        method: "GET",
        autoCall: true,
    });

    const {
        data: editprojectResponse,
        loading: editprojectDataLoading,
        error: editprojectDataError,
        callApi: editProject,
    } = useApi("/api/edit-project", {
        method: "POST",
        autoCall: false,
    });

    const {
        data: projectResponse,
        loading: projectDataLoading,
        error: projectDataError,
    } = useApi("/api/get-project", {
        method: "GET",
        autoCall: true,
        dependencies: [callApiAgain],
    });


    useEffect(() => {
        if (!projectResponse?.projects || !projectId) return;

        const data = projectResponse.projects.find(
            (p: any) => p.project_id === Number(projectId)
        );

        if (!data) return;

        setData({
            project_id: data.project_id,
            project_name: data.project_name,
            owner_name: data.owner_name,
            priority: data.priority,
            cost_center: data.cost_center_code,
            department: data.department,
            status: data.status,
            attachment_file: "",
        });

        setStartDate(dayjs(data.start_date));
        setEndDate(dayjs(data.end_date));
        setTags(data.tags?.map((tag: any) => tag.tag_name) || []);
        setContent(data.description);

    }, [projectResponse?.projects, projectId]);


    const config = useMemo(
        () => ({
            readonly: false,
        }),
        []
    );

    const getInputData = (
        e: React.ChangeEvent<any>
    ) => {
        const { name, value, files } = e.target;
        if (name === "attachment_file") {
            setData({ ...data, [name]: files[0] });
        }
        else {
            setData({ ...data, [name]: value });
        }
    };

    const costCenterOptions =
        costcenter?.data.map((item: any) => ({
            value: item.costcentre_code,
            label: `${item.costcentre_code} - ${item.costcentre_name}`,
        })) || [];

    const editData = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("project_id", data.project_id);
        formData.append("project_name", data.project_name);
        formData.append("owner_name", data.owner_name);
        formData.append("description", content);
        tags.forEach((tag) => {
            formData.append("tag_name[]", tag);
        });
        formData.append(
            "start_date",
            startDate ? startDate.format("YYYY-MM-DD") : ""
        );
        formData.append("end_date", endDate ? endDate.format("YYYY-MM-DD") : "");
        formData.append("priority", data.priority);
        formData.append("cost_center_code", data.cost_center);
        formData.append("department", data.department);
        formData.append("status", data.status);
        formData.append("attachment_file", data.attachment_file);
        try {
            const response = await editProject(formData);
            if (response.success) {
                Swal.fire({
                    icon: "success",
                    title: "Project Update",
                    text:
                        response?.message || "Your project has been updated successfully.",
                    confirmButtonColor: "#3085d6",
                });
                document.getElementById("closed_edit_modal")?.click();
                setCallApiAgain(callApiAgain + 1);
                setData({
                    project_id: "",
                    project_name: "",
                    owner_name: "",
                    priority: "",
                    cost_center: "",
                    department: "",
                    attachment_file: "",
                    status: ""
                });
                setTags([]);
                setStartDate(null);
                setEndDate(null);
                setContent("");
            }
        } catch (error) {
            console.error(error);
        }
    };
    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="container-fluid">

                    {/* Page Header */}
                    <div className="row mb-4">
                        <div className="col-12 d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">Edit Project</h4>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => window.history.back()}
                            >
                                <i className="ti ti-arrow-left me-1" />
                                Back
                            </button>
                        </div>
                    </div>

                    {/* Card */}
                    <div className="card">
                        <div className="card-body p-0">

                            {/* Tabs Header */}
                            <div className="contact-grids-tab p-3 pb-0">
                                <ul className="nav nav-underline">
                                    <li className="nav-item">
                                        <span className="nav-link active">
                                            Project Information
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* Form */}
                            <form onSubmit={editData}>
                                <div className="p-3">
                                    <div className="row">

                                        {/* Project Name */}
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Project Name<sup className="text-danger">*</sup>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="project_name"
                                                    value={data.project_name}
                                                    onChange={getInputData}
                                                    placeholder="Project Name"
                                                />
                                            </div>
                                        </div>

                                        {/* Owner */}
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Owner<sup className="text-danger">*</sup>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="owner_name"
                                                    value={data.owner_name}
                                                    onChange={getInputData}
                                                    placeholder="Enter Owner Name"
                                                />
                                            </div>
                                        </div>

                                        {/* Cost Center */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Cost Center Code<sup className="text-danger">*</sup>
                                                </label>
                                                <Select
                                                    options={costCenterOptions}
                                                    value={costCenterOptions.find(
                                                        (option: any) => option.value === data.cost_center
                                                    )}
                                                    onChange={(selected: any) =>
                                                        setData((prev) => ({
                                                            ...prev,
                                                            cost_center: selected?.value || "",
                                                        }))
                                                    }
                                                    placeholder="Select Cost Center"
                                                />
                                            </div>
                                        </div>

                                        {/* Department */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Department<sup className="text-danger">*</sup>
                                                </label>
                                                <Select
                                                    options={DepartmentSelectData}
                                                    value={DepartmentSelectData.find(
                                                        (option: any) => option.value === data.department
                                                    )}
                                                    onChange={(selected: any) =>
                                                        getInputData({
                                                            target: {
                                                                name: "department",
                                                                value: selected?.value || "",
                                                            },
                                                        } as any)
                                                    }
                                                    placeholder="Select Department"
                                                />
                                            </div>
                                        </div>

                                        {/* Start Date */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Start Date<sup className="text-danger">*</sup>
                                                </label>
                                                <DatePicker
                                                    className="form-control"
                                                    value={startDate}
                                                    format={{ format: "DD-MM-YYYY", type: "mask" }}
                                                    onChange={(date) => setStartDate(date)}
                                                    disabledDate={(current) =>
                                                        current && current.isBefore(dayjs().startOf("day"))
                                                    }
                                                    placeholder="DD-MM-YYYY"
                                                />
                                            </div>
                                        </div>

                                        {/* End Date */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    End Date<sup className="text-danger">*</sup>
                                                </label>
                                                <DatePicker
                                                    className="form-control"
                                                    value={endDate}
                                                    format={{ format: "DD-MM-YYYY", type: "mask" }}
                                                    onChange={(date) => setEndDate(date)}
                                                    disabledDate={(current: any) =>
                                                        current &&
                                                        (current.isBefore(dayjs().startOf("day")) ||
                                                            (startDate && current.isBefore(startDate)))
                                                    }
                                                    placeholder="DD-MM-YYYY"
                                                />
                                            </div>
                                        </div>

                                        {/* Tags */}
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Tags<sup className="text-danger">*</sup>
                                                </label>
                                                <CommonTagsInput
                                                    value={tags}
                                                    onChange={setTags}
                                                    placeholder="Add new"
                                                />
                                                <small className="text-danger">
                                                    Press enter to add more
                                                </small>
                                            </div>
                                        </div>

                                        {/* Priority */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Priority<sup className="text-danger">*</sup>
                                                </label>
                                                <select
                                                    className="form-select"
                                                    name="priority"
                                                    value={data.priority}
                                                    onChange={getInputData}
                                                >
                                                    <option value="">Select Priority</option>
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Upload */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Upload File</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    name="attachment_file"
                                                    onChange={getInputData}
                                                />
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">
                                                    Description<sup className="text-danger">*</sup>
                                                </label>
                                                <JoditEditor
                                                    ref={editor}
                                                    value={content}
                                                    config={config}
                                                    onBlur={(newContent) => setContent(newContent)}
                                                />
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    name="status"
                                                    value={data.status}
                                                    onChange={getInputData}
                                                >
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="border-top p-3 text-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary me-2"
                                        onClick={() => window.history.back()}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Edit Project
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default EditProject;